import { useMemo } from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { WhatsNewItem } from '@platform/client/features/feature-config';

export interface NewsItem {
  title: string;
  description: string;
  url: string;
  date: Dayjs | null;
}

export const getNewsItemTranslationPrefix = (
  newsItemId: string,
  hasAccess: boolean,
) => {
  const access = hasAccess ? 'hasAccess' : 'lacksAccess';
  return `${newsItemId}.${access}`;
};

export const useNewsItems = (featureReleases: WhatsNewItem[]) => {
  const { t } = useTranslation('app', { keyPrefix: 'appBar.notifications' });

  const newsItems = useMemo<NewsItem[]>(
    () =>
      [
        ...featureReleases
          .map(({ newsItemId, hasAccess, date }) => ({
            translationPrefix: getNewsItemTranslationPrefix(
              newsItemId,
              hasAccess,
            ),
            date,
          }))
          .map(({ translationPrefix, date }) => {
            const { title, description, url } = t(translationPrefix, {
              returnObjects: true,
            }) as { title: string; description: string; url: string };

            return {
              title,
              description,
              url,
              date,
            };
          }),
      ]
        .filter((newsItem) => newsItem.title)
        .filter(
          (newsItem) =>
            (newsItem.date && newsItem.date <= dayjs()) ||
            newsItem.date === null,
        ),
    [featureReleases, t],
  );

  return newsItems;
};
